import { FloorplanItemReference } from "@domain/project/floorplan-item-reference";
import { Floorplan } from "@domain/project/floorplan/floorplan";
import { FloorplanEventType } from "@domain/project/floorplan/floorplan-event";
import { FloorplanNode } from "@domain/project/floorplan/floorplan-node";
import { FloorplanPlaceholderPositionKind } from "@domain/project/floorplan/floorplan-placeholder-position-kind";
import { PlaceholderProduct } from "@domain/project/floorplan/placeholder-product";
import { IsPastDate } from "@utils/class-validator/class-validator-constraints";
import { Exclude, Expose, Transform, Type } from "class-transformer";
import { IsBoolean, IsDate, IsOptional, IsString, IsUUID, MaxLength } from "class-validator";

export type FloorplanGroupedProduct = FloorplanItem & { products: PlaceholderProduct[] };

type FloorplanItemDuplicationState = {
  displayName: string;
  itemTextVisible: boolean;
  locked: boolean;
  notes: string;
};

export abstract class FloorplanItem extends FloorplanNode {
  @Exclude()
  protected _floorplan!: Floorplan;

  @IsBoolean()
  @Expose({ name: "locked" })
  @Transform((params) => params.value || false, { toClassOnly: true })
  protected _locked: boolean;

  @IsBoolean()
  @Expose({ name: "itemTextVisible" })
  @Transform((params) => params.value || true, { toClassOnly: true })
  protected _itemTextVisible: boolean;

  @Type(() => Date)
  @Transform((params) => params.value || new Date(), { toClassOnly: true })
  @IsDate()
  @IsPastDate()
  @Expose({ name: "lockTimestamp" })
  protected _lockTimestamp: Date = new Date();

  @IsString()
  @IsOptional()
  @Expose({ name: "positionId" })
  protected _positionId?: string;

  @IsString()
  @MaxLength(2000, { message: "Notes must not exceed 2000 characters in length" })
  @Expose({ name: "notes" })
  protected _notes: string = "";

  @IsString()
  @IsOptional()
  @Expose({ name: "displayName" })
  protected _displayName?: string;

  @IsUUID()
  public readonly id: string;

  protected constructor(
    floorplan: Floorplan,
    id: string,
    x: number,
    y: number,
    notes: string = "",
    locked: boolean = false,
    itemTextVisible: boolean = true,
    lockTimestamp: Date = new Date(),
    displayName: string = "",
  ) {
    super(x, y);
    this._floorplan = floorplan;
    this._lockTimestamp = lockTimestamp;
    this._locked = locked;
    this._itemTextVisible = itemTextVisible;
    this._notes = notes;
    this.id = id;
    this._displayName = displayName;
  }

  init(floorplan: Floorplan) {
    this._floorplan = floorplan;
  }

  get positionId(): string | undefined {
    return this._positionId;
  }

  set positionId(positionId) {
    if (this._positionId === positionId) return;
    this._positionId = positionId;
    this._floorplan.publishUpdate(FloorplanEventType.ITEM_POSITION_ID_CHANGED, this);
  }

  get notes(): string {
    return this._notes;
  }

  set notes(notes: string) {
    if (this._notes === notes) return;
    this._notes = notes;
    this._floorplan.publishUpdate(FloorplanEventType.ITEM_UPDATED, this);
  }

  get locked(): boolean {
    return this._locked;
  }

  set locked(locked: boolean) {
    if (this._locked === locked) return;
    this.setLockedWithoutRefresh(locked);
    this.refreshFloorplanLockState();
  }

  get itemTextVisible(): boolean {
    return this._itemTextVisible;
  }

  set itemTextVisible(visible: boolean) {
    this._itemTextVisible = visible;
  }

  get lockTimestamp(): Date {
    return this._lockTimestamp;
  }

  updateItemTextVisibility(visible: boolean) {
    if (this._itemTextVisible === visible) return;
    this._itemTextVisible = visible;
    this._floorplan.publishUpdate(FloorplanEventType.ITEM_TEXT_VISIBILITY_CHANGED, { floorplanItem: this, visible: visible });
  }

  setLockedWithoutRefresh(locked: boolean) {
    if (this._locked === locked) return;
    this._locked = locked;
    this._lockTimestamp = new Date();
    this._floorplan.publishUpdate(FloorplanEventType.ITEM_LOCK_CHANGED, this);
  }

  override updatePosition(x: number, y: number) {
    super.updatePosition(x, y);
    this._floorplan.publishUpdate(FloorplanEventType.ITEM_UPDATED, this);
  }

  get displayName(): string {
    return this._displayName || "";
  }

  set displayName(displayName: string) {
    if (this._displayName === displayName) return;
    this._displayName = displayName;
    this._floorplan.publishUpdate(FloorplanEventType.ITEM_DISPLAY_NAME_CHANGED, this);
  }

  protected abstract refreshFloorplanLockState(): void;

  abstract setItemTextVisibility(): void;

  duplicate(x: number, y: number): FloorplanItem | undefined {
    return this.createDuplicateSnapshot()?.(x, y);
  }

  createDuplicateSnapshot(): ((x: number, y: number) => FloorplanItem) | undefined {
    const createRawDuplicate = this.createRawDuplicate();
    const registerDuplicate = this.registerDuplicate();
    if (!createRawDuplicate && !registerDuplicate) {
      return undefined;
    }

    if (!createRawDuplicate || !registerDuplicate) {
      throw new Error(`${this.constructor.name} duplication contract is incomplete`);
    }

    const copyDuplicateStateTo = this.copyDuplicateStateTo();
    const afterDuplicateAdded = this.afterDuplicateAdded();

    return (x: number, y: number) => {
      const duplicate = createRawDuplicate(x, y);
      duplicate.setItemTextVisibility();
      copyDuplicateStateTo?.(duplicate);
      return registerDuplicate(duplicate, afterDuplicateAdded);
    };
  }

  getDuplicateTargetCollection(): FloorplanItem[] | undefined {
    return undefined;
  }

  isGroupedProduct(): this is FloorplanGroupedProduct {
    return false;
  }

  supportsPlaceholderPositionIds(): boolean {
    return false;
  }

  usesPlaceholderPrefixPositionId(): boolean {
    const kind = this.getPlaceholderPositionKind();
    if (!kind) {
      return false;
    }
    const prefix = this._floorplan.project.getPlaceholderPositionIdPrefix(kind);
    return !!prefix && !!this.positionId?.startsWith(prefix);
  }

  refreshPlaceholderPositionIds(positionNumber?: number): boolean {
    void positionNumber;
    return false;
  }

  protected captureDuplicationState(): FloorplanItemDuplicationState {
    return {
      displayName: this.displayName,
      itemTextVisible: this.itemTextVisible,
      locked: this.locked,
      notes: this.notes,
    };
  }

  protected applyDuplicationState(target: FloorplanItem, state: FloorplanItemDuplicationState) {
    target._displayName = state.displayName;
    target._notes = state.notes;
    target._itemTextVisible = state.itemTextVisible;
    target._locked = state.locked;
    if (state.locked) {
      target._lockTimestamp = new Date();
    }
  }

  protected copyDuplicateStateTo(): (duplicate: FloorplanItem) => void {
    const state = this.captureDuplicationState();
    const copySpecificDuplicateStateTo = this.copySpecificDuplicateStateTo();

    return (duplicate: FloorplanItem) => {
      this.applyDuplicationState(duplicate, state);
      copySpecificDuplicateStateTo?.(duplicate);
    };
  }

  protected copySpecificDuplicateStateTo(): ((duplicate: FloorplanItem) => void) | undefined {
    return undefined;
  }

  protected afterDuplicateAdded(): ((duplicate: FloorplanItem) => void) | undefined {
    return undefined;
  }

  protected afterGroupedDuplicateAdded(
    afterDuplicateAdded?: (duplicate: FloorplanItem) => void,
  ): ((duplicate: FloorplanItem) => void) | undefined {
    const sourceGroup = this._floorplan.project.groups.find((g) =>
      g.references.some((r) => r.floorplanId === this._floorplan.id && r.floorplanProductItemId === this.id),
    );
    const sourceReference = this._floorplan.project.getProductItemReference(this._floorplan.id, this.id);

    return (duplicate: FloorplanItem) => {
      afterDuplicateAdded?.(duplicate);
      if (sourceGroup && sourceReference) {
        const duplicateReference = new FloorplanItemReference(this._floorplan.id, duplicate.id, sourceReference.kind);
        this._floorplan.project.addProductItemReference(duplicateReference);
        sourceGroup.addReference(duplicateReference);
      }
    };
  }

  protected registerDuplicateInFloorplan<T extends FloorplanItem>(
    duplicate: T,
    targetCollection: FloorplanItem[],
    addedEventType: FloorplanEventType,
    refreshPositionIds?: () => void,
    afterDuplicateAdded?: (duplicate: T) => void,
  ): T {
    return this._floorplan.registerDuplicate(
      duplicate,
      targetCollection,
      addedEventType,
      () => this.refreshFloorplanLockState(),
      refreshPositionIds,
      afterDuplicateAdded,
    );
  }

  protected registerDuplicate():
    ((duplicate: FloorplanItem, afterDuplicateAdded?: (duplicate: FloorplanItem) => void) => FloorplanItem) | undefined {
    return undefined;
  }

  getPlaceholderPositionKind(): FloorplanPlaceholderPositionKind | undefined {
    return undefined;
  }

  protected createRawDuplicate(): ((x: number, y: number) => FloorplanItem) | undefined {
    return undefined;
  }

  abstract delete(): void;
}
